"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OWN_ROLE_CHANGE_MESSAGE = exports.ownRoleChangeLogout = void 0;
exports.logoutAfterOwnRoleChange = logoutAfterOwnRoleChange;
exports.saveStaffMemberAccountData = saveStaffMemberAccountData;
exports.mergeAccountIntoRow = mergeAccountIntoRow;
exports.makeStaffMemberEditHandler = makeStaffMemberEditHandler;
exports.StaffMemberEditModalButton = StaffMemberEditModalButton;
const react_1 = __importDefault(require("react"));
const GeneralModalButtons_1 = require("../../../View/Modals/GeneralModalButtons");
const personsV2Helpers_1 = require("../../../Persons/personsV2Helpers");
const StaffMembersController_1 = require("../StaffMembersController");
const StaffMemberModalBody_1 = require("./StaffMemberModalBody");
const UserModalButtons_1 = require("./UserModalButtons");
const StaffMemberValidationSchema_1 = require("./StaffMemberValidationSchema");
/**
 * Wylogowanie po zmianie WŁASNEJ roli (D-PER-10, 2026-09-04). Serwer skasował sesję
 * wołającego, żeby nowa rola obowiązywała od razu; bez tego kroku człowiek dostawał raport
 * błędu serwera i przy następnym kliknięciu lądował na ekranie logowania bez słowa wyjaśnienia.
 *
 * `alert`, nie toast: blokuje stronę do kliknięcia, więc żadne równoległe żądanie nie zdąży
 * dostać 401 i przeładować strony, zanim człowiek przeczyta, co się stało. Zależności są
 * podmienne, żeby test nie przeładowywał strony testowej.
 */
exports.ownRoleChangeLogout = {
    notify: (message) => window.alert(message),
    reload: () => window.location.reload(),
};
exports.OWN_ROLE_CHANGE_MESSAGE = "Zmieniłeś własną rolę systemową. Nowe uprawnienia zaczną obowiązywać po ponownym zalogowaniu - za chwilę zobaczysz ekran logowania.";
function logoutAfterOwnRoleChange() {
    exports.ownRoleChangeLogout.notify(exports.OWN_ROLE_CHANGE_MESSAGE);
    window.sessionStorage.clear();
    exports.ownRoleChangeLogout.reload();
}
/**
 * Domknięcie zapisu z panelu uprawnień. Woła się PO zapisie flag (PUT /admin/staffMember):
 * 1. konto trasą v2 (rola, e-mail systemowy, FIDman - flaga zawsze jawnie, także false),
 * 2. przypisania projektów.
 *
 * Kolejność „flagi, potem konto" jest celowa: zmiana roli trasą v2 zakłada domyślne
 * flagi dla roli (INSERT IGNORE). Gdy wiersz flag już stoi, nic nie nadpisuje.
 *
 * To ta sama trasa konta, której używa ekran użytkowników - jedyna, która unieważnia
 * sesje po zmianie roli i kolejkuje push `user.upsert` do FIDmana. Panel nie pisze roli
 * własną drogą (PER-1). Zwraca błędy zamiast rzucać: lista musi najpierw dostać zapisane
 * flagi, a dopiero potem modal pokazuje, czego NIE zapisał.
 */
async function saveStaffMemberAccountData(personId, values) {
    const errors = [];
    const v2Result = await (0, personsV2Helpers_1.savePersonV2AccountAndProfile)(personId, (0, personsV2Helpers_1.buildAccountPayload)(values), {}, "StaffMembers");
    errors.push(...v2Result.errors);
    // D-PER-10: zapis WŁASNEJ roli - serwer skasował sesję wołającego. Przypisania projektów
    // poszłyby już w 401 (i przeładowały stronę bez słowa wyjaśnienia), więc tu kończymy;
    // komunikat i wylogowanie robi handler.
    if (v2Result.account?._selfSessionRevoked) {
        return { account: v2Result.account, errors, selfSessionRevoked: true };
    }
    try {
        await (0, personsV2Helpers_1.saveProjectAssignments)({
            id: personId,
            systemRoleId: values.systemRoleId,
            _projectAssignments: values._projectAssignments,
        });
    }
    catch (error) {
        errors.push(`Przypisania projektów: ${error instanceof Error ? error.message : String(error)}`);
    }
    return { account: v2Result.account, errors };
}
/**
 * Wiersz listy po domknięciu: odpowiedź na zapis flag niesie konto sprzed zapisu konta,
 * więc rolę, e-mail i FIDmana bierzemy z odpowiedzi trasy v2. Bez konta (zapis konta
 * padł) wiersz zostaje taki, jaki jest - to prawdziwy stan bazy.
 */
function mergeAccountIntoRow(row, account) {
    if (!account)
        return row;
    return {
        ...row,
        _systemRoleId: account.systemRoleId ?? null,
        _systemEmail: account.systemEmail ?? null,
        _fidmanEnabled: !!account.fidmanEnabled,
    };
}
/**
 * Handler `onEdit` modalu uprawnień, wyciągnięty z komponentu, żeby dało się go testować.
 *
 * `submitted` (drugi argument z GeneralModal) to wiersz scalony z formularzem - stąd
 * rola, e-mail systemowy, FIDman i projekty. `editedObject` to odpowiedź serwera na
 * zapis flag: świeży odczyt wiersza, który tych pól NIE odbija. Bez `submitted`
 * nie ma czego zapisać, a wysłanie domyślnych wartości wyłączyłoby komuś FIDmana.
 */
function makeStaffMemberEditHandler(onEdit) {
    return async function handleEdit(editedObject, submitted) {
        const personId = editedObject.personId ?? editedObject.id;
        if (!submitted || !personId) {
            onEdit(editedObject);
            throw new Error("Flagi zapisano, ale konto NIE: formularz nie przekazał wartości konta.");
        }
        const { account, errors, selfSessionRevoked } = await saveStaffMemberAccountData(personId, submitted);
        if (selfSessionRevoked) {
            // Bez odczytu wiersza z serwera (poszedłby w 401) i bez rzucania: to nie jest
            // błąd, tylko koniec sesji. Okno się zamyka, lista dostaje wiersz scalony
            // z odpowiedzią i zaraz znika razem z sesją.
            onEdit(mergeAccountIntoRow(editedObject, account));
            logoutAfterOwnRoleChange();
            return;
        }
        // Świeży odczyt wiersza z serwera zamiast scalania odpowiedzi: po zapisie konta serwer
        // od razu próbuje wysłać je do FIDmana (dostawa po commicie, PRZED odpowiedzią), więc
        // dopiero odczyt listy niesie stan tej wysyłki (D-PER-8). Odczyt nie jest zapisem -
        // jego błąd nie może przykryć błędu zapisu konta; wtedy scalamy jak dawniej.
        const fresh = await (0, UserModalButtons_1.fetchStaffMemberRow)(personId).catch(() => null);
        // Lista czyta repository.items, nie argument callbacku - wiersz podmieniamy
        // w repozytorium, inaczej pokazałaby konto sprzed zapisu.
        const row = fresh ?? mergeAccountIntoRow(editedObject, account);
        if (row !== editedObject && row.id !== undefined) {
            StaffMembersController_1.staffMembersRepository.replaceItemById(row.id, row);
            StaffMembersController_1.staffMembersRepository.replaceCurrentItemById(row.id, row);
            StaffMembersController_1.staffMembersRepository.saveToSessionStorage();
        }
        onEdit(row);
        // Dopiero teraz błąd: modal zostaje otwarty i pokazuje, czego NIE zapisał.
        // „Uprawnienia", nie „Dane osoby": to, co zapisał pierwszy PUT, to flagi.
        (0, personsV2Helpers_1.throwOnSaveErrors)(errors, "Uprawnienia");
    };
}
/**
 * Tylko edycja. Nie ma przycisku dodawania, bo panel nie zakłada ludzi -
 * uprawnienia przypina się do osób już istniejących w systemie.
 */
function StaffMemberEditModalButton({ modalProps: { onEdit, initialData }, }) {
    return (react_1.default.createElement(GeneralModalButtons_1.GeneralEditModalButton, { modalProps: {
            onEdit: makeStaffMemberEditHandler(onEdit),
            ModalBodyComponent: StaffMemberModalBody_1.StaffMemberModalBody,
            modalTitle: "Uprawnienia osoby",
            repository: StaffMembersController_1.staffMembersRepository,
            initialData: initialData,
            makeValidationSchema: StaffMemberValidationSchema_1.makeStaffMemberValidationSchema,
        }, buttonProps: {
            buttonVariant: "outline-success",
        } }));
}
